import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { PRIVATE_PATH } from '../../../constants';
import { useGetOnDemandStaffByManagementCompanyQuery } from '../../../redux/managementCompanyWorkforce';
import { useGetOnDemandStaffProfessionalCredentialsQuery } from '../../../redux/onDemandStaffProfessionalCredentials';
import { useGetOnDemandStaffProfessionalShiftHistoryQuery } from '../../../redux/onDemandStaffProfessionalShiftHistory';
import SidebarWrapper from '../../../sections/SidebarWrapper';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import CredentialStatusPill from '../../../components/CredentialStatusPill/CredentialStatusPill';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import OnDemandStaffCredentialData from './OnDemandStaffCredentialData';
import OnDemandStaffShiftHistory from './shift-history/OnDemandStaffShiftHistory';
import ShiftHistoryDateFilter from './shift-history/ShiftHistoryDateFilter';

// Reuse the existing credentials-page styling so this ShiftMed page matches the workforce credentials design.
import '../../flex/professionals/credentials/FlexProfessionalCredentials.scss';
import './OnDemandStaffProfessionalDetails.scss';

const EMPTY_DATE_RANGE = { from: null, to: null };

// Read-only profile for a ShiftMed on-demand professional, with two pages:
// - Credentials (On Demand Staff professionals only): the facility-packet credential requirements applicable to the
//   professional; the Type(s) pills are the required subtypes they satisfy with a verified, unexpired credential.
// - Shift History (professionals who worked under the company at the user's contact facilities): their worked
//   shifts, with each shift's credential packet.
// The professional's name, On Demand Staff flag and worked flag are passed via router state from the ShiftMed list; on
// a direct URL / refresh (no state) they are read from the ShiftMed workforce list.
const OnDemandStaffProfessionalDetails = () => {
	const history = useHistory();
	const location = useLocation();
	// The originating MC travels in the route itself (rather than redux, which resets on a hard refresh) since
	// the credentials endpoint is MC-scoped: guessing a different, merely-authorized MC would silently return
	// that MC's facility-packet requirements instead of the ones this professional was opened under.
	const { managementCompanyId, caregiverId } = useParams();

	const [expandedRows, setExpandedRows] = useState(null);
	// Not persisted: leaving the page drops the filter.
	const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);

	const professionalPath = `${PRIVATE_PATH.WORKFORCE_PAGE}/${managementCompanyId}/on-demand-staff/${caregiverId}`;
	const credentialsPath = `${professionalPath}/credentials`;
	const shiftHistoryPath = `${professionalPath}/shift-history`;
	const isShiftHistoryPage = location.pathname === shiftHistoryPath;

	const { data: credentials, isFetching } = useGetOnDemandStaffProfessionalCredentialsQuery(
		{ caregiverId, managementCompanyId },
		{ skip: !managementCompanyId || isShiftHistoryPage, refetchOnMountOrArgChange: true }
	);

	// Only on the Shift History page, where it feeds the date filter. Unfiltered, so the filter does not depend on the
	// applied date range. Shares its cache entry with the Shift History table while no range is applied.
	const { data: shiftHistory } = useGetOnDemandStaffProfessionalShiftHistoryQuery(
		{ caregiverId, managementCompanyId },
		{ skip: !managementCompanyId || !isShiftHistoryPage }
	);

	// Only credentials the professional actually holds (a Type(s) pill) are expandable — an empty row has no
	// instance detail to show.
	const allowExpansion = useCallback((row) => row.types.length > 0, []);

	const rowExpansionTemplate = useCallback(
		(row) => (
			<OnDemandStaffCredentialData
				caregiverId={caregiverId}
				managementCompanyId={managementCompanyId}
				credentialType={row.credentialType}
			/>
		),
		[caregiverId, managementCompanyId]
	);

	// The expander column only reacts to clicks on the chevron itself; clicking anywhere else on the row must
	// also toggle it. PrimeReact skips onRowClick for clicks on the chevron button (or any other clickable
	// element) already, so this never double-toggles.
	const toggleRowExpansion = useCallback(
		(row) => {
			if (!allowExpansion(row)) {
				return;
			}
			setExpandedRows((previousExpandedRows) => {
				const nextExpandedRows = { ...(previousExpandedRows || {}) };
				if (nextExpandedRows[row.credentialType]) {
					delete nextExpandedRows[row.credentialType];
				} else {
					nextExpandedRows[row.credentialType] = true;
				}
				return nextExpandedRows;
			});
		},
		[allowExpansion]
	);

	// On a direct URL / refresh, router state is gone, so resolve the professional from the ShiftMed workforce list
	// (same MC, already used/cached by the tab) instead of leaving the title without a name.
	const { data: shiftMedStaff } = useGetOnDemandStaffByManagementCompanyQuery(managementCompanyId, {
		skip: !managementCompanyId
	});
	const listedProfessional = useMemo(
		() => shiftMedStaff?.find((professional) => String(professional.caregiverId) === String(caregiverId)),
		[shiftMedStaff, caregiverId]
	);

	const resolvedName = location.state?.professionalName || listedProfessional?.name;
	const professionalName = resolvedName && resolvedName !== '-' ? resolvedName : '';
	const isOnDemandStaff = location.state?.isOnDemandStaff ?? listedProfessional?.isOnDemandStaff;
	// Worked under the company at the user's contact facilities in the last year — decides the Shift History item
	// without fetching the shift history itself.
	const hasWorked = location.state?.hasWorked ?? listedProfessional?.hasWorked;

	const dateFilter = shiftHistory?.hasPastShiftsPermission ? (
		<ShiftHistoryDateFilter dateRange={dateRange} onApply={setDateRange} />
	) : null;

	// A page's own item always shows while on it, so its title renders before the data that decides the sidebar
	// has loaded.
	const sidebarItems = [
		(isOnDemandStaff || !isShiftHistoryPage) && {
			label: 'Credentials',
			editTitle: professionalName ? `Credentials - ${professionalName}` : 'Credentials',
			isEditing: true,
			path: credentialsPath,
			className: 'flex-credentials-title'
		},
		(hasWorked || isShiftHistoryPage) && {
			label: 'Shift History',
			editTitle: professionalName ? `Shift History - ${professionalName}` : 'Shift History',
			isEditing: true,
			path: shiftHistoryPath,
			className: 'flex-credentials-title',
			additionalAction: dateFilter
		}
	].filter(Boolean);

	const handleBackToWorkforce = () => history.push(PRIVATE_PATH.WORKFORCE_PAGE);

	const typesTemplate = useCallback(
		(row) => (
			<div className="status-container">
				{row.types.map((type) => (
					<CredentialStatusPill key={type.code} status="VERIFIED" title={type.name} />
				))}
			</div>
		),
		[]
	);

	const credentialsPage = (
		<div className="flex-professionals-credentials on-demand-staff-credentials">
			<p className="credentials-info">
				Only <strong>non-expired</strong>, <strong>verified</strong> credentials will show for ShiftMed professionals
			</p>
			<DataTable
				value={credentials || []}
				tableStyle={{ tableLayout: 'fixed' }}
				size="small"
				stripedRows
				dataKey="credentialType"
				loading={isFetching}
				loadingIcon={<LoadingSpinner />}
				emptyMessage="No results found"
				expandedRows={expandedRows}
				onRowToggle={(event) => setExpandedRows(event.data)}
				onRowClick={(event) => toggleRowExpansion(event.data)}
				rowExpansionTemplate={rowExpansionTemplate}
				rowClassName={(row) => (allowExpansion(row) ? 'expandable-row' : '')}
			>
				<Column expander={allowExpansion} bodyClassName="expandable" style={{ width: '5%' }} />
				<Column field="name" header="Credential" style={{ width: '35%' }} sortable />
				<Column header="Type(s)" style={{ width: '60%' }} body={typesTemplate} />
			</DataTable>
		</div>
	);

	return (
		<SidebarWrapper
			sidebarTitle="Professional"
			sidebarItems={sidebarItems}
			backTitle="Back to Workforce"
			backAction={handleBackToWorkforce}
			className="flex-professionals-sidebar on-demand-staff-professional"
		>
			<WithOnlyOnDesktop>
				{isShiftHistoryPage ? (
					<OnDemandStaffShiftHistory
						managementCompanyId={managementCompanyId}
						caregiverId={caregiverId}
						dateRange={dateRange}
					/>
				) : (
					credentialsPage
				)}
			</WithOnlyOnDesktop>
		</SidebarWrapper>
	);
};

export default OnDemandStaffProfessionalDetails;
